import { PaymentModePolicy } from "constants/charge/PaymentModePolicy";
import { CollectionDetailEntity } from "domain/entity/Collection/CollectionDetailEntity";
import { CounterCustomerChargeMaintenanceConstant } from "presentation/constant/CounterCustomerCharge/CounterCustomerChargeMaintenanceConstant";
import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCounterCustomerChargeMaintenanceTracked } from "presentation/store/CounterCustomerCharge/CounterCustomerChargeMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { FieldType, HPHButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";


const PaymentModeAndAdditionEditPanel: React.FC = () => {
    const [counterCustomerChargeState] = useCounterCustomerChargeMaintenanceTracked();
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const { paymentModeState, dynamicOptions } = counterCustomerChargeState;
    const { editingEntity, allFormState, tableData, paymentModeHeader, isBankCode, isCreditCardNo, isPayOrderAmount, isPayOrderNo } = paymentModeState;
    const messageBarVM = useMessageBarVM();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const savingRef = useRef(false);
    const COUNTER_CUSTOMER_CHARGE_CONSTANT = CounterCustomerChargeMaintenanceConstant.PAYMENT;
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;

    const handleSaveClick = useCallback(async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setIsLoading(true);
        try {
            let totalInvAmt = 0;
            tableData.forEach(item => {
                if (item.paymentAmount) {
                    totalInvAmt = (Math.round(totalInvAmt * 100) + Math.round(item.paymentAmount * 100)) / 100;
                }
            })
            if (paymentModeHeader.invoiceAmount && totalInvAmt >= paymentModeHeader.invoiceAmount) {
                messageBarVM.showError("All invoices are full paid.");
                return;
            }
            let colleDtl: CollectionDetailEntity = {
                ...editingEntity,
                draftInvoiceNos: paymentModeHeader.draftInvoiceNo
            }
            if (editingEntity && editingEntity.paymentMode === 'PO' && editingEntity.payOrderAmount && editingEntity.paymentAmount &&
                Number(editingEntity.payOrderAmount) < Number(editingEntity.paymentAmount)) {
                messageBarVM.showError("Pay order amount must greater or equal to payment amount.");
                return;
            }
            const res = await counterCustomerChargeVM.onPaymentModeAddSave(colleDtl);
            if (res && res.success) {
                if (res.data) {
                    messageBarVM.showError(res.data);
                } else {
                    messageBarVM.showSuccess('Save Successfully.');
                    if (paymentModeHeader.docHdrId) {
                        await counterCustomerChargeVM.onSearchPaymentMode(paymentModeHeader.docHdrId);
                    }
                }
            } else {
                if (res.data && res.data === 'missing') {
                    // messageBarVM.showError('Please input the missing value.');
                } else {
                    messageBarVM.showError('Save Failed.');
                }
            }
        } finally {
            setIsLoading(false);
            savingRef.current = false;
        }
    }, [counterCustomerChargeVM, editingEntity, tableData, paymentModeHeader, messageBarVM]);

    const handClose = useCallback(() => {
        counterCustomerChargeVM.onPaymentModeAddClose();
    }, [counterCustomerChargeVM]);

    const memoPaymentMode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "140px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={editingEntity?.paymentMode || ''}
                fieldValue={editingEntity?.paymentMode}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.PAYMENT_MODE}
                isSaveClicked={paymentModeState.isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'paymentMode'}
                maxLength={60}
                options={operatingUnit === "OICT" ? PaymentModePolicy().getModeItemDroOptsForOICT() : PaymentModePolicy().getModeItemDroOptsForAQCT()}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onPaymentModeAddFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, editingEntity?.paymentMode, COUNTER_CUSTOMER_CHARGE_CONSTANT.PAYMENT_MODE, paymentModeState.isSaveClicked, operatingUnit, counterCustomerChargeVM])

    const memoBankCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "140px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isBankCode}
                isShowOptional={false}
                readOnlyValue={editingEntity?.bankCode || ''}
                fieldValue={editingEntity?.bankCode}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.BANK_CODE}
                isSaveClicked={paymentModeState.isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'bankCode'}
                maxLength={60}
                options={dynamicOptions.bankCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onPaymentModeAddFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.BANK_CODE, editingEntity?.bankCode, paymentModeState.isSaveClicked, counterCustomerChargeVM, allFormState, dynamicOptions.bankCodeDropdownOptions, isBankCode])

    const memoPaymentAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "140px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={editingEntity?.paymentAmount?.toString() || ''}
                fieldValue={editingEntity?.paymentAmount?.toString()}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.PAYMENT_AMOUNT}
                isSaveClicked={paymentModeState.isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'paymentAmount'}
                maxLength={13}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onPaymentModeAddFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, COUNTER_CUSTOMER_CHARGE_CONSTANT.PAYMENT_AMOUNT, editingEntity?.paymentAmount, paymentModeState.isSaveClicked, counterCustomerChargeVM])

    const memoPayOrderAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "140px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isPayOrderAmount}
                isShowOptional={false}
                readOnlyValue={editingEntity?.payOrderAmount?.toString() || ''}
                fieldValue={editingEntity?.payOrderAmount?.toString()}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.PAY_ORDER_AMOUNT}
                isSaveClicked={paymentModeState.isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'payOrderAmount'}
                maxLength={13}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onPaymentModeAddFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, COUNTER_CUSTOMER_CHARGE_CONSTANT.PAY_ORDER_AMOUNT, editingEntity?.payOrderAmount, paymentModeState.isSaveClicked, counterCustomerChargeVM, isPayOrderAmount])

    const memoPayOrderNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "140px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isPayOrderNo}
                isShowOptional={false}
                readOnlyValue={editingEntity?.payOrderNo || ''}
                fieldValue={editingEntity?.payOrderNo}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.PAY_ORDER_NO}
                isSaveClicked={paymentModeState.isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'payOrderNo'}
                maxLength={20}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onPaymentModeAddFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, COUNTER_CUSTOMER_CHARGE_CONSTANT.PAY_ORDER_NO, editingEntity?.payOrderNo, paymentModeState.isSaveClicked, counterCustomerChargeVM, isPayOrderNo])

    const memoCreditCardNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "140px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isCreditCardNo}
                isShowOptional={false}
                readOnlyValue={editingEntity?.creditCardNo || ''}
                fieldValue={editingEntity?.creditCardNo}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.CREDIT_CARD_NO}
                isSaveClicked={paymentModeState.isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'creditCardNo'}
                maxLength={20}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onPaymentModeAddFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, COUNTER_CUSTOMER_CHARGE_CONSTANT.CREDIT_CARD_NO, editingEntity?.creditCardNo, paymentModeState.isSaveClicked, counterCustomerChargeVM, isCreditCardNo])

    return <>
        <div className='side-form-content-wrapper' style={{ height: 'calc(100vh - 1vh)' }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{'Payment Mode Additional Info'}</SidebarTitle>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ height: '23vh', maxHeight: '23vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoPaymentMode}
                            {memoBankCode}
                            {memoPaymentAmount}
                            {memoPayOrderAmount}
                            {memoPayOrderNo}
                            {memoCreditCardNo}
                        </CriteriaItemContainer>
                    </div>
                </div>
            </div>

            <div className='im-charge-data-search-criteria-buttons'>
                {
                    <>
                        <HPHButton label={'Cancel'} size={'Small'} theme={'Secondary'} onClick={handClose} />
                        <HPHButton label={'Save'} size={'Small'} theme={'Primary'} disabled={isLoading} onClick={() => handleSaveClick()} />
                    </>
                }
            </div>
        </div>
    </>;
}

export default memo(PaymentModeAndAdditionEditPanel);
